import { Fragment, useEffect, useState } from "react";
import "../admin/productlist.css";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  addArchiveProduct,
  cloneProduct,
  getSellerProducts,
} from "../../actions/productActions";
import {
  clearError,
  clearProductCloned,
  clearProductDeleted,
  clearProductArchive,
} from "../../slices/singleProductSlice";
import Loader from "../layouts/Loader";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Pagination from "react-js-pagination";
import SellerSidebar from "./SellerSidebar";
import {
  faCartShopping,
  faFilter,
  faPencil,
  faSearch,
  faDashboard,
  faList,
  faShoppingBag,
  faSort,
} from "@fortawesome/free-solid-svg-icons";
import Drawer from "@mui/material/Drawer";
import { Dropdown } from "react-bootstrap";
import { Modal, Box, Typography, Button } from "@mui/material";
import MetaData from "../layouts/MetaData";
import XLSX from "xlsx-js-style";

export default function SellerProducts() {
  const {
    loading = true,
    products = [],
    sellerProductCount,
    isProductDeleted,
    resPerPage,
    isProductCreated,
    isProductUpdated,
    error,
  } = useSelector((state) => state.productState);
  const [searchKeyword] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [clocreProductId, setclocreProductId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch();
  function debounce(func, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  }
  const archiveHandler = (e, id) => {
    e.target.disabled = true;
    dispatch(addArchiveProduct(id));
  };
  const setCurrentPageNo = (pageNo) => {
    setCurrentPage(pageNo);
  };
  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setCurrentPageNo(1);
    dispatch(getSellerProducts(searchKeyword, status));
  };

  const filterLabel = {
    approved: "Approved",
    pending: "Pending",
    rejected: "Rejected",
    deactivated: "Deactivated",
    "": "All",
  }[filterStatus];

  useEffect(() => {
    const debouncedSearch = debounce(() => {
      dispatch(getSellerProducts(clocreProductId, filterStatus, 1)); // always reset to 1
      setCurrentPage(1);
    }, 500); // 500ms debounce

    debouncedSearch();

    // Cleanup on unmount
    return () => {
      debouncedSearch.cancel && debouncedSearch.cancel();
    };
  }, [clocreProductId, filterStatus, dispatch]);

  useEffect(() => {
    if (error) {
      toast(error, {
        type: "error",
        onOpen: () => {
          dispatch(clearError());
        },
      });
      return;
    }
    if (isProductDeleted) {
      toast("Product Deleted Succesfully!", {
        type: "success",
        onOpen: () => dispatch(clearProductDeleted()),
      });
      return;
    }
    if (isProductCreated) {
      toast.success("Product Cloned Successfully!", {
        onOpen: () => dispatch(clearProductCloned()),
      });
      return;
    }
    if (isProductUpdated) {
      toast.success("Product Archived Successfully!", {
        onOpen: () => dispatch(clearProductArchive()),
      });
      return;
    }
    dispatch(getSellerProducts(searchKeyword, filterStatus, currentPage));
  }, [
    dispatch,
    error,
    isProductDeleted,
    isProductUpdated,
    isProductCreated,
    searchKeyword,
    filterStatus,
    currentPage,
  ]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1000);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1000);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [productToClone, setProductToClone] = useState(null);
  const [cloneEvent, setCloneEvent] = useState(null); // store the event
  const handleCloneClick = (e, id) => {
    setCloneEvent(e); // store the event
    setProductToClone(id);
    setCloneModalOpen(true);
  };
  const confirmClone = () => {
    cloneEvent.target.disabled = true;
    dispatch(cloneProduct(productToClone));
    setCloneModalOpen(false);
    setProductToClone(null);
    setCloneEvent(null);
  };
  const cancelClone = () => {
    setCloneModalOpen(false);
    setProductToClone(null);
    setCloneEvent(null);
  };
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [productToArchive, setProductToArchive] = useState(null);
  const [archiveEvent, setArchiveEvent] = useState(null);
  const handleArchiveClick = (e, id) => {
    setArchiveEvent(e);
    setProductToArchive(id);
    setArchiveModalOpen(true);
  };
  const confirmArchive = () => {
    archiveHandler(archiveEvent, productToArchive);
    setArchiveModalOpen(false);
    setProductToArchive(null);
    setArchiveEvent(null);
  };
  const cancelArchive = () => {
    setArchiveModalOpen(false);
    setProductToArchive(null);
    setArchiveEvent(null);
  };

  const downloadExcel = () => {
    const excelData = products.map((product, index) => ({
      "S.No": index + 1,

      "Product ID": product.clocreProductId,

      SKU:
        product?.variants?.length > 0
          ? product.variants
              .map((v) => v?.sku)
              .filter(Boolean)
              .join(" , ")
          : product?.sku || "-",

      "Product Name": product.name,

      Status: product.status || "Pending",

      "Created Date": new Date(product.createdAt).toLocaleDateString(),
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    worksheet["!cols"] = [
      { wch: 10 },
      { wch: 22 },
      { wch: 35 },
      { wch: 60 },
      { wch: 18 },
      { wch: 18 },
    ];

    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({
          r: row,
          c: col,
        });

        if (!worksheet[cellAddress]) continue;

        worksheet[cellAddress].s = {
          font: {
            bold: row === 0,
            color: {
              rgb: row === 0 ? "FFFFFF" : "000000",
            },
            sz: 12,
          },

          fill:
            row === 0
              ? {
                  fgColor: { rgb: "FFAD63" },
                }
              : undefined,

          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: true,
          },

          border: {
            top: {
              style: "thin",
              color: { rgb: "D3D3D3" },
            },
            bottom: {
              style: "thin",
              color: { rgb: "D3D3D3" },
            },
            left: {
              style: "thin",
              color: { rgb: "D3D3D3" },
            },
            right: {
              style: "thin",
              color: { rgb: "D3D3D3" },
            },
          },
        };
      }
    }

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Seller Products");

    const today = new Date();

    const formattedDate = `${today.getDate()}-${
      today.getMonth() + 1
    }-${today.getFullYear()}_${today.getHours()}-${today.getMinutes()}`;

    XLSX.writeFile(workbook, `Seller_Products_${formattedDate}.xlsx`, {
      cellStyles: true,
    });
  };

  return (
    <Fragment>
      <MetaData title="Seller Product List | GLOCRE Private Limited" />
      <section className="seller-product-list-section ">
        <div className="row container-fluid">
          <div className="col-12 col-md-2">
            <SellerSidebar />
          </div>
          <div className="col-12 col-lg-10 col-md-12 pr-0">
            <Link to="/">
              <div className="mobile-logo">
                <img
                  src={require("../../images/procure-g-logo.png")}
                  alt="glocre"
                />
              </div>
            </Link>
            <div className="breadcrumbWrapperr">
              {isMobile ? (
                <div className="row mobile-topbar">
                  <div className="col-10">
                    <ul className="breadcrumb breadcrumb2 mb-0">
                      <li>
                        <Link to="/seller/dashboard" style={{ color: "#fff" }}>
                          Dashboard
                        </Link>
                      </li>
                      <li>Product List</li>
                    </ul>
                  </div>
                  <div className="col-2 p-0 d-flex justify-content-center align-items-center">
                    <button className="fab" onClick={toggleDrawer}>
                      <FontAwesomeIcon icon={faList} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="row dash-navbar-big-glc small-sticky-navbar">
                  <div className="col-lg-3 col-md-12">
                    <ul className="breadcrumb breadcrumb2 mb-0">
                      <li>
                        <Link to="/seller/dashboard">Dashboard</Link>
                      </li>
                      <li>Product List</li>
                    </ul>
                  </div>
                  <div className="col-lg-7 col-md-6 d-flex justify-content-end align-items-end">
                    <div className="dash-cont-glc">
                      <div className="row">
                        <div className="topnav">
                          <div className="search-container">
                            <form className="d-flex">
                              <input
                                type="text"
                                placeholder="Search"
                                name="search"
                                value={clocreProductId}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const cleanedValue = value.replace(
                                    /[^a-zA-Z0-9 ]/g,
                                    "",
                                  );
                                  setclocreProductId(cleanedValue);
                                }}
                              />
                              <button type="submit">
                                <FontAwesomeIcon icon={faSearch} />
                              </button>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-1 col-md-2 d-flex justify-content-center align-items-end">
                    <Dropdown className="d-inline">
                      <Dropdown.Toggle
                        variant="default"
                        id="dropdown-basic"
                        className="custom-filter-toggle"
                      >
                        <FontAwesomeIcon icon={faFilter} />
                        <span className="ms-2">{filterLabel}</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => handleFilterChange("approved")}
                          className="text-dark"
                        >
                          approved
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleFilterChange("pending")}
                          className="text-dark"
                        >
                          pending
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleFilterChange("rejected")}
                        >
                          Rejected
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleFilterChange("")}
                          className="text-dark"
                        >
                          All
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              )}
              {/* Search, Filter & Avatar Row (For Mobile) */}
              {isMobile && (
                <div className="row mobile-bottombar">
                  <div className="col-9 col-md-10 pr-0">
                    <div className="search-container">
                      <form className="d-flex">
                        <input
                          type="text"
                          placeholder="Search"
                          name="search"
                          value={clocreProductId}
                          onChange={(e) => {
                            const value = e.target.value;
                            const cleanedValue = value.replace(
                              /[^a-zA-Z0-9 ]/g,
                              "",
                            );
                            setclocreProductId(cleanedValue);
                          }}
                        />
                        <button type="submit">
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </form>
                    </div>
                  </div>
                  <div className="col-2 text-center">
                    <Dropdown className="d-inline">
                      <Dropdown.Toggle
                        variant="default text-white"
                        id="dropdown-basic"
                        className="text-dark dropdown1 icon-list-filter-procureg"
                        style={{
                          backgroundImage: "none",
                          border: "none",
                          boxShadow: "none",
                        }}
                      >
                        <FontAwesomeIcon icon={faFilter} />
                        <span className="ms-2">{filterLabel}</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => handleFilterChange("approved")}
                          className="text-dark"
                        >
                          approved
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleFilterChange("pending")}
                          className="text-dark"
                        >
                          pending
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleFilterChange("rejected")}
                        >
                          Rejected
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleFilterChange("")}
                          className="text-dark"
                        >
                          All
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              )}
              <Drawer
                open={isDrawerOpen}
                onClose={toggleDrawer}
                direction="right"
                className="drawer"
              >
                <div className="drawer-header">SELLER DASHBOARD</div>
                <div className="drawer-content">
                  <ul className="drawer-links">
                    <li>
                      <Link to="/seller/dashboard">
                        <FontAwesomeIcon icon={faDashboard} className="me-2" />
                        Dashboard
                      </Link>
                    </li>
                    <li>
                      <Link to="/seller/products">
                        <FontAwesomeIcon
                          icon={faCartShopping}
                          className="me-2"
                        />
                        Product List
                      </Link>
                    </li>
                    <li>
                      <Link to="/seller/products/create">
                        <FontAwesomeIcon
                          icon={faShoppingBag}
                          className="me-2"
                        />
                        Create Product
                      </Link>
                    </li>
                    <li>
                      <Link to="/seller/orders">
                        <FontAwesomeIcon icon={faSort} className="me-2" />
                        Order List
                      </Link>
                    </li>
                    <li>
                      <Link to="/seller/archive/product">
                        <FontAwesomeIcon icon={faSort} className="me-2" />
                        Archived Products
                      </Link>
                    </li>
                  </ul>
                </div>
              </Drawer>
            </div>

            <div
              className="d-flex justify-content-between align-items-center flex-wrap"
              style={{ marginTop: "40px" }}
            >
              <h3 className="" style={{ color: "#ffad63", margin: 0 }}>
                PRODUCT LIST
              </h3>

              <button
                onClick={downloadExcel}
                className="btn"
                style={{
                  backgroundColor: "#2f4d2a",
                  color: "#fff",
                  border: "none",
                  padding: "7px 16px",
                  margin: "0px 18px",
                  borderRadius: "6px",
                  fontWeight: "500",
                }}
              >
                Download Excel
              </button>
            </div>

            <section className="cartWrapper mt-4">
              <div className="table-responsive" style={{ overflowX: "auto" }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: "130px" }}>ID</th>
                      <th style={{ minWidth: "170px" }}>SKU</th>
                      <th style={{ minWidth: "200px" }}>Name</th>
                      <th style={{ minWidth: "140px" }}>Date</th>
                      <th style={{ minWidth: "140px" }}>Status</th>
                      <th style={{ minWidth: "150px" }}>Update Product</th>
                      <th style={{ minWidth: "150px" }}>Clone</th>
                      <th style={{ minWidth: "150px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="text-center">
                          <Loader />
                        </td>
                      </tr>
                    ) : products.length === 0 ? (
                      <div className="text-center py-5">
                        <p style={{ color: "#8c8c8c", fontSize: "18px" }}>
                          You have no products. Please create one.
                        </p>
                        <Link to="/seller/products/create">
                          <button
                            className="btn mt-3"
                            style={{
                              backgroundColor: "#ffad63",
                              color: "#fff",
                            }}
                          >
                            Create Product
                          </button>
                        </Link>
                      </div>
                    ) : (
                      products.map((product) => (
                        <tr key={product._id}>
                          <td>
                            <span style={{ color: "#888888" }}>
                              {product.clocreProductId}
                            </span>
                          </td>

                          <td>
                            <div className="d-flex align-items-center">
                              <span style={{ color: "#8c8c8c" }}>
                                {product?.variants?.length > 0
                                  ? product.variants
                                      .map((v) => v?.sku)
                                      .filter(Boolean) // removes null / undefined / ""
                                      .join(" & ")
                                  : product?.sku || "-"}
                              </span>
                            </div>
                          </td>

                          <td>
                            <div className="d-flex align-items-center">
                              <span
                                style={{ fontSize: "16px", color: "#888888" }}
                              >
                                {product.name}
                              </span>
                            </div>
                          </td>

                          <td>
                            <span style={{ color: "#888888" }}>
                              {new Date(product.createdAt).toLocaleDateString()}
                            </span>
                          </td>

                          <td
                            style={{
                              color:
                                product.status === "approved"
                                  ? "#2f4d2a"
                                  : "#ffad63",
                              fontSize: "16px",
                            }}
                          >
                            {product.status || "Pending"}
                          </td>

                          <td>
                            <Link
                              to={`/seller/product/${product._id}`}
                              className="btn"
                              style={{
                                backgroundColor: "#ffad63",
                                color: "#fff",
                              }}
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </Link>
                          </td>

                          <td>
                            <button
                              onClick={(e) => handleCloneClick(e, product._id)}
                              style={{
                                backgroundColor: "#ffad63",
                                color: "#fff",
                                fontSize: "15px",
                                padding: "5px",
                                borderRadius: "7px",
                              }}
                            >
                              Clone
                            </button>
                          </td>

                          <Modal open={cloneModalOpen} onClose={cancelClone}>
                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                bgcolor: "background.paper",
                                p: 4,
                                borderRadius: 2,
                                width: 300,
                                border: "none",
                                outline: "none",
                              }}
                            >
                              <Typography
                                variant="h6"
                                mb={2}
                                align="center"
                                fontSize={17}
                                color="#8c8c8c"
                              >
                                Want to clone this item?
                              </Typography>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                              >
                                <Button
                                  onClick={confirmClone}
                                  className="left-but"
                                  sx={{
                                    margin: "3px",
                                  }}
                                >
                                  Yes
                                </Button>
                                <Button
                                  onClick={cancelClone}
                                  className="right-but"
                                  sx={{
                                    margin: "3px",
                                  }}
                                >
                                  No
                                </Button>
                              </Box>
                            </Box>
                          </Modal>
                          <td>
                            <button
                              onClick={(e) =>
                                handleArchiveClick(e, product._id)
                              }
                              style={{
                                backgroundColor: "#2f4d2a",
                                color: "#fff",
                                fontSize: "15px",
                                padding: "5px",
                                borderRadius: "7px",
                              }}
                            >
                              Archive
                            </button>
                          </td>
                          <Modal
                            open={archiveModalOpen}
                            onClose={cancelArchive}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                bgcolor: "background.paper",
                                p: 4,
                                borderRadius: 2,
                                width: 300,
                                border: "none",
                                outline: "none",
                              }}
                            >
                              <Typography
                                variant="h6"
                                mb={2}
                                align="center"
                                fontSize={17}
                                color="#8c8c8c"
                              >
                                Want to Unarchive this item?(item will be moved
                                to pending state)
                              </Typography>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                              >
                                <Button
                                  onClick={confirmArchive}
                                  className="left-but"
                                  sx={{
                                    margin: "3px",
                                  }}
                                >
                                  Yes
                                </Button>
                                <Button
                                  onClick={cancelArchive}
                                  className="right-but"
                                  sx={{
                                    margin: "3px",
                                  }}
                                >
                                  No
                                </Button>
                              </Box>
                            </Box>
                          </Modal>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {sellerProductCount > 0 && sellerProductCount > resPerPage ? (
                <div className="d-flex justify-content-center mt-5 tab-slider">
                  <Pagination
                    activePage={currentPage}
                    onChange={setCurrentPageNo}
                    totalItemsCount={sellerProductCount}
                    itemsCountPerPage={resPerPage}
                    nextPageText={"Next"}
                    firstPageText={"First"}
                    lastPageText={"Last"}
                    itemClass={"page-item"}
                    linkClass={"page-link"}
                  />
                </div>
              ) : null}
            </section>
          </div>
        </div>
      </section>
    </Fragment>
  );
}
